import ExcelJS from "exceljs";

export type ViewMode =
  | "Day"
  | "Week"
  | "Month"
  | "QuarterYear"
  | "Year";

type Resource = {
  id: string;
  name: string;
  type: "user" | "project" | "task";
  user?: { userAvailability?: { startDate?: string; endDate?: string }[] };
  userAvailability?: { startDate?: string; endDate?: string }[];
  children?: Resource[];
};

type DailyWorkload = Map<string, Map<string, number>>;

type FlatRow = {
  node: Resource;
  level: number;
  id: string;
};

const pad = (n: number) => String(n).padStart(2, "0");

/** Format Date → "dd/mm/yyyy" */
const fmt = (d: Date) =>
  `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;

const toLocal = (d: Date | string) => {
  const x = new Date(d);
  return new Date(x.getFullYear(), x.getMonth(), x.getDate());
};

const isWorkingDayFn = (nonWorkingDays: number[], holidays: Date[]) => {
  const holidaySet = new Set(holidays.map(fmt));
  const nwSet = new Set(nonWorkingDays);
  const cache = new Map<string, boolean>();
  return (date: Date): boolean => {
    const k = fmt(date);
    if (cache.has(k)) return cache.get(k)!;
    const v = !nwSet.has(date.getDay()) && !holidaySet.has(k);
    cache.set(k, v);
    return v;
  };
};

const workableHours = (
  periodStart: Date,
  viewMode: ViewMode,
  isWorking: (d: Date) => boolean
): number => {
  if (viewMode === "Day") return isWorking(periodStart) ? 8 : 0;

  const end = new Date(periodStart);
  if (viewMode === "Week") end.setDate(end.getDate() + 6);
  else if (viewMode === "Month") end.setMonth(end.getMonth() + 1, 0);
  else if (viewMode === "QuarterYear") end.setMonth(end.getMonth() + 3, 0);
  else if (viewMode === "Year") end.setFullYear(end.getFullYear() + 1, 0, 0);

  let days = 0;
  const cur = new Date(periodStart);
  while (cur <= end) {
    if (isWorking(cur)) days++;
    cur.setDate(cur.getDate() + 1);
  }
  return days * 8;
};

const colLabel = (date: Date, viewMode: ViewMode): string => {
  if (viewMode === "Day") return `${fmt(date)}`;
  if (viewMode === "Week") return `Wk ${fmt(date)}`;
  if (viewMode === "Month") {
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  }
  if (viewMode === "QuarterYear") {
    const q = Math.floor(date.getMonth() / 3) + 1;
    return `Q${q} ${date.getFullYear()}`;
  }
  return String(date.getFullYear());
};

/** Check both resource.user.userAvailability and resource.userAvailability (merged from org) */
function getAvailabilityList(resource: Resource): { startDate?: string; endDate?: string }[] {
  const fromUser = resource.user?.userAvailability;
  const fromResource = resource.userAvailability;
  if (fromUser?.length) return fromUser;
  if (fromResource?.length) return fromResource;
  return [];
}

const isAbsentOn = (resource: Resource, date: Date): boolean => {
  const avail = getAvailabilityList(resource);
  if (!avail.length) return false;
  return avail.some((r) => {
    const s = r.startDate ? toLocal(new Date(r.startDate)) : null;
    const e = r.endDate ? toLocal(new Date(r.endDate)) : null;
    const d = toLocal(date);
    if (s && e) return d >= s && d <= e;
    if (s) return d.getTime() === s.getTime();
    return false;
  });
};

const flatten = (resources: Resource[]): FlatRow[] => {
  const result: FlatRow[] = [];
  const walk = (node: Resource, level = 0) => {
    result.push({ node, level, id: node.id });
    if (node.children?.length) {
      node.children.forEach((c) => walk(c, level + 1));
    }
  };
  resources.forEach((r) => walk(r));
  return result;
};

// ExcelJS uses ARGB (alpha first). For solid fill use FF + hex.
const COLORS = {
  overload:   "FFFFC1CE",
  loaded:     "FFDDDFFE",
  absent:     "FFCCCCCC",
  nonWorking: "FFEFEFEF",
  headerBg:   "FF535EF9",
  userRowBg:  "FFF5F5FF",
  buttonInactive: "FFE0E0E5",
  buttonActive:   "FF535EF9",
  white:      "FFFFFFFF",
  textDark:   "FF333333",
  textWhite:  "FFFFFFFF",
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function getDailyDates(dates: Date[]): Date[] {
  if (dates.length === 0) return [];
  const sorted = [...dates].map(toLocal).sort((a, b) => a.getTime() - b.getTime());
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  const out: Date[] = [];
  const cur = new Date(min);
  while (cur <= max) {
    out.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

function getWeeklyDates(dates: Date[]): Date[] {
  if (dates.length === 0) return [];
  const sorted = [...dates].map(toLocal).sort((a, b) => a.getTime() - b.getTime());
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  const out: Date[] = [];
  const cur = new Date(min);
  while (cur <= max) {
    out.push(new Date(cur));
    cur.setDate(cur.getDate() + 7);
  }
  return out;
}

function getMonthlyDates(dates: Date[]): Date[] {
  if (dates.length === 0) return [];
  const sorted = [...dates].map(toLocal).sort((a, b) => a.getTime() - b.getTime());
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  const out: Date[] = [];
  const cur = new Date(min.getFullYear(), min.getMonth(), 1);
  const end = new Date(max.getFullYear(), max.getMonth(), 1);
  while (cur <= end) {
    out.push(new Date(cur));
    cur.setMonth(cur.getMonth() + 1);
  }
  return out;
}

function getYearlyDates(dates: Date[]): Date[] {
  if (dates.length === 0) return [];
  const sorted = [...dates].map(toLocal).sort((a, b) => a.getTime() - b.getTime());
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  const out: Date[] = [];
  for (let y = min.getFullYear(); y <= max.getFullYear(); y++) {
    out.push(new Date(y, 0, 1));
  }
  return out;
}

function getPeriodHoursAndAbsent(
  userWorkload: Map<string, number> | undefined,
  periodStart: Date,
  viewMode: "Week" | "Month" | "Year",
  resource: Resource
): { hours: number; anyAbsent: boolean } {
  let hours = 0;
  let anyAbsent = false;
  const end = new Date(periodStart);
  if (viewMode === "Week") {
    end.setDate(end.getDate() + 6);
  } else if (viewMode === "Month") {
    end.setMonth(end.getMonth() + 1, 0);
  } else {
    end.setFullYear(end.getFullYear() + 1, 0, 0);
  }
  const cur = new Date(periodStart);
  while (cur <= end) {
    const key = fmt(cur);
    if (isAbsentOn(resource, cur)) anyAbsent = true;
    hours += userWorkload?.get(key) ?? 0;
    cur.setDate(cur.getDate() + 1);
  }
  return { hours, anyAbsent };
}

export interface DownloadWorkloadXlsxOptions {
  resources: Resource[];
  dailyWorkload: DailyWorkload;
  dates: Date[];
  inPercentage: boolean;
  nonWorkingDays: number[];
  holidays: Date[];
  filename?: string;
}

type SheetViewMode = "Day" | "Week" | "Month" | "Year";

function parseEffortForOverload(cellValue: string, inPercentage: boolean): boolean {
  if (inPercentage) {
    const pct = parseFloat(String(cellValue).replace("%", ""));
    return !Number.isNaN(pct) && pct > 100;
  }
  const parts = String(cellValue).split("/").map((s) => parseFloat(s.trim()));
  return parts.length >= 2 && parts[0] > parts[1];
}

function applyCellStyle(
  cell: ExcelJS.Cell,
  opts: {
    bgColor: string;
    bold?: boolean;
    fgColor?: string;
    horizontal?: "left" | "center";
  }
) {
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: opts.bgColor },
  };
  cell.font = {
    name: "Arial",
    size: 10,
    bold: opts.bold ?? false,
    color: { argb: opts.fgColor ?? COLORS.textDark },
  };
  cell.alignment = {
    horizontal: opts.horizontal ?? "left",
    vertical: "middle",
    wrapText: true,
  };
  cell.border = {
    top: { style: "thin", color: { argb: "FFE7E7E7" } },
    bottom: { style: "thin", color: { argb: "FFE7E7E7" } },
    left: { style: "thin", color: { argb: "FFE7E7E7" } },
    right: { style: "thin", color: { argb: "FFE7E7E7" } },
  };
}

/** Add view-switch row with button-like cells and internal hyperlinks (whole cell clickable) */
function addButtonRow(
  worksheet: ExcelJS.Worksheet,
  sheetName: string,
  periodDates: Date[]
) {
  const row = worksheet.getRow(1);
  row.height = 24;
  const viewCell = row.getCell(1);
  viewCell.value = "View:";
  applyCellStyle(viewCell, { bgColor: COLORS.buttonInactive, bold: true });

  const sheets: { name: string; col: number }[] = [
    { name: "Daily", col: 2 },
    { name: "Weekly", col: 3 },
    { name: "Monthly", col: 4 },
    { name: "Yearly", col: 5 },
  ];
  sheets.forEach(({ name, col }) => {
    const cell = row.getCell(col);
    const isActive = sheetName === name;
    cell.value = {
      text: name,
      hyperlink: `#'${name}'!A1`,
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: isActive ? COLORS.buttonActive : COLORS.buttonInactive },
    };
    cell.font = {
      name: "Arial",
      size: 10,
      bold: true,
      color: { argb: isActive ? COLORS.textWhite : COLORS.textDark },
    };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border = {
      top: { style: "medium", color: { argb: "FFB0B0B0" } },
      bottom: { style: "medium", color: { argb: "FFB0B0B0" } },
      left: { style: "medium", color: { argb: "FFB0B0B0" } },
      right: { style: "medium", color: { argb: "FFB0B0B0" } },
    };
  });
  // Fill rest of row so it looks like one bar
  for (let c = 6; c <= 2 + periodDates.length; c++) {
    const cell = row.getCell(c);
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: COLORS.buttonInactive },
    };
    cell.border = {
      top: { style: "thin", color: { argb: "FFE7E7E7" } },
      bottom: { style: "thin", color: { argb: "FFE7E7E7" } },
      left: { style: "thin", color: { argb: "FFE7E7E7" } },
      right: { style: "thin", color: { argb: "FFE7E7E7" } },
    };
  }
}

export async function downloadWorkloadXlsx(opts: DownloadWorkloadXlsxOptions): Promise<void> {
  const {
    resources,
    dailyWorkload,
    dates,
    inPercentage,
    nonWorkingDays,
    holidays,
    filename = "workload.xlsx",
  } = opts;
  const isWorking = isWorkingDayFn(nonWorkingDays, holidays);
  const flatRowsAll = flatten(resources);

  // keep only user rows and immediate project rows (drop task-level rows)
  const flatRows = flatRowsAll.filter(
    (fr) => fr.level === 0 || (fr.level === 1 && fr.node.type === "project")
  );

  const dailyDates = getDailyDates(dates);
  const weeklyDates = getWeeklyDates(dates);
  const monthlyDates = getMonthlyDates(dates);
  const yearlyDates = getYearlyDates(dates);

  const workbook = new ExcelJS.Workbook();
  const sheetConfigs: { name: string; viewMode: SheetViewMode; periodDates: Date[] }[] = [
    { name: "Daily", viewMode: "Day", periodDates: dailyDates },
    { name: "Weekly", viewMode: "Week", periodDates: weeklyDates },
    { name: "Monthly", viewMode: "Month", periodDates: monthlyDates },
    { name: "Yearly", viewMode: "Year", periodDates: yearlyDates },
  ];

  for (const { name: sheetName, viewMode: sheetViewMode, periodDates } of sheetConfigs) {
    const worksheet = workbook.addWorksheet(sheetName, { views: [{ state: "frozen", ySplit: sheetViewMode === "Day" ? 3 : 2, xSplit: 2 }] });

    // Column widths
    worksheet.getColumn(1).width = 22;
    worksheet.getColumn(2).width = 28;
    periodDates.forEach((_, i) => {
      worksheet.getColumn(3 + i).width = sheetViewMode === "Day" ? 12 : 14;
    });

    // Row 1: buttons
    addButtonRow(worksheet, sheetName, periodDates);

    const headerRowIndex = 2;
    const weekdayRowIndex = sheetViewMode === "Day" ? 3 : -1;
    const firstDataRow = sheetViewMode === "Day" ? 4 : 3;

    // Header row (dates)
    const headerRow = worksheet.getRow(headerRowIndex);
    const headerViewMode: ViewMode =
      sheetViewMode === "Day"
        ? "Day"
        : sheetViewMode === "Week"
        ? "Week"
        : sheetViewMode === "Month"
        ? "Month"
        : "Year";
    const headerCells = [
      "Member",
      "Project",
      ...periodDates.map((d) => colLabel(d, headerViewMode)),
    ];
    headerCells.forEach((val, c) => {
      const cell = headerRow.getCell(c + 1);
      cell.value = val;
      applyCellStyle(cell, { bgColor: COLORS.headerBg, bold: true, fgColor: COLORS.textWhite, horizontal: c >= 2 ? "center" : "left" });
    });

    // Weekday row (Daily only)
    if (sheetViewMode === "Day") {
      const wdRow = worksheet.getRow(weekdayRowIndex);
      wdRow.getCell(1).value = "";
      wdRow.getCell(2).value = "";
      applyCellStyle(wdRow.getCell(1), { bgColor: COLORS.headerBg, fgColor: COLORS.textWhite });
      applyCellStyle(wdRow.getCell(2), { bgColor: COLORS.headerBg, fgColor: COLORS.textWhite });
      periodDates.forEach((d, j) => {
        const cell = wdRow.getCell(3 + j);
        cell.value = WEEKDAYS[d.getDay()];
        applyCellStyle(cell, { bgColor: COLORS.headerBg, bold: true, fgColor: COLORS.textWhite, horizontal: "center" });
      });
    }

    // Data rows
    flatRows.forEach((fr, rowIdx) => {
      const r = firstDataRow + rowIdx;
      const row = worksheet.getRow(r);
      const indent = "  ".repeat(Math.max(0, fr.level - 1));
      const colA = fr.level === 0 ? fr.node.name : "";
      const colB = fr.level === 0 ? "" : `${indent}${fr.node.name}`;
      const c1 = row.getCell(1);
      const c2 = row.getCell(2);
      c1.value = colA;
      c2.value = colB;
      applyCellStyle(c1, { bgColor: fr.level === 0 ? COLORS.userRowBg : COLORS.white, bold: fr.level === 0 });
      applyCellStyle(c2, { bgColor: fr.level === 0 ? COLORS.userRowBg : COLORS.white, bold: fr.level === 0 });

      const userWorkload = dailyWorkload.get(fr.node.id);
      periodDates.forEach((date, dateColIdx) => {
        const cell = row.getCell(3 + dateColIdx);
        let val: string | number = "";
        if (sheetViewMode === "Day") {
          const dateKey = fmt(date);
          const hours = userWorkload?.get(dateKey) ?? 0;
          if (fr.level !== 0 && isAbsentOn(fr.node, date)) val = "A";
          else if (!isWorking(date)) val = "";
          else if (hours === 0) val = "";
          else if (fr.level === 0) val = "";
          else if (inPercentage) val = `${Math.round(hours)}%`;
          else {
            const maxH = workableHours(date, "Day", isWorking);
            val = `${Number(hours.toFixed(1))} / ${maxH}`;
          }
        } else {
          const viewModeStr: ViewMode =
            sheetViewMode === "Week"
              ? "Week"
              : sheetViewMode === "Month"
              ? "Month"
              : "Year";

          const { hours } = getPeriodHoursAndAbsent(
            userWorkload,
            date,
            sheetViewMode as "Week" | "Month" | "Year",
            fr.node
          );

          if (hours === 0) val = "";
          else if (fr.level === 0) val = "";
          else if (inPercentage) {
            const maxH = workableHours(date, viewModeStr, isWorking);
            const workingDays = maxH / 8;
            val = workingDays > 0 ? `${Math.round(hours / workingDays)}%` : "";
          } else {
            const maxH = workableHours(date, viewModeStr, isWorking);
            val = `${Number(hours.toFixed(1))} / ${maxH}`;
          }
        }
        cell.value = val;

        let bgColor = COLORS.white;
        const strVal = String(val);
        if (strVal === "A") bgColor = COLORS.absent;
        else if (strVal !== "") {
          bgColor = parseEffortForOverload(strVal, inPercentage) ? COLORS.overload : COLORS.loaded;
        } else if (sheetViewMode === "Day" && !isWorking(date)) {
          bgColor = COLORS.nonWorking;
        }
        applyCellStyle(cell, { bgColor, horizontal: "center" });
      });
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
